"use client";

import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { ResponsiveModal, useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, AlertTriangle } from "lucide-react";
import {
  type GenericCsvImportModalProps,
  type CsvImportResult,
} from "./types";
import { validateCsvData, parseCsv } from "./utils";
import { SampleTemplateCard } from "./SampleTemplateCard";
import { UploadZone } from "./UploadZone";
import { CsvPreview } from "./CsvPreview";
import { ImportProgress } from "./ImportProgress";
import { ImportResults } from "./ImportResults";

const GenericCsvImportModal = <T extends Record<string, any>>({
  modalTitle,
  modalDescription,
  columns,
  generateSampleCsv,
  useCreateHook,
  transformRowData,
  onSuccess,
  triggerButton,
}: GenericCsvImportModalProps<T>) => {
  const { openModal, closeModal, isModalOpen } = useGlobalModalStack();
  const [csvData, setCsvData] = useState<T[]>([]);
  const [importResults, setImportResults] = useState<CsvImportResult[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [currentRow, setCurrentRow] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const createMutation = useCreateHook();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const csvText = e.target?.result as string;
        const parsedData = parseCsv<T>(csvText);
        setCsvData(parsedData);

        const errors = validateCsvData(parsedData, columns);
        setValidationErrors(errors);
      };
      reader.readAsText(file);
    },
    [columns]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
    multiple: false,
  });

  const importData = async () => {
    if (validationErrors.length > 0) return;

    setIsImporting(true);
    setImportResults([]);
    setTotalRows(csvData.length);
    setCurrentRow(0);

    const results: CsvImportResult[] = [];

    for (let i = 0; i < csvData.length; i++) {
      setCurrentRow(i + 1);
      const row = csvData[i];

      try {
        const transformedData = transformRowData(row);

        await new Promise((resolve) => {
          createMutation.mutate(transformedData, {
            onSuccess: (data: any) => {
              results.push({
                success: true,
                id: data.id,
                row: i + 2,
              });
              resolve(true);
            },
            onError: (error: any) => {
              results.push({
                success: false,
                error: error.message || "Failed to create item",
                row: i + 2,
              });
              resolve(false);
            },
          });
        });

        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        results.push({
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
          row: i + 2,
        });
      }
    }

    setImportResults(results);
    setIsImporting(false);
    if (onSuccess) onSuccess();
  };

  const resetForm = () => {
    setCsvData([]);
    setImportResults([]);
    setValidationErrors([]);
    setCurrentRow(0);
    setTotalRows(0);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      openModal(MODAL_IDS.importExport.csvImport);
    } else {
      closeModal(MODAL_IDS.importExport.csvImport);
    }
  };

  const isOpen = isModalOpen(MODAL_IDS.importExport.csvImport);

  return (
    <>
      <div
        onClick={() => {
          handleOpenChange(true);
        }}
      >
        {triggerButton}
      </div>

      <ResponsiveModal
        globalModalId={MODAL_IDS.importExport.csvImport}
        title={modalTitle}
        description={modalDescription}
        maxWidth="4xl"
      >
        <div className="space-y-6">
          <SampleTemplateCard columns={columns} generateSampleCsv={generateSampleCsv} />

          <UploadZone
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
          />

          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">Validation Errors:</p>
                  <ul className="text-sm space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <CsvPreview csvData={csvData} columns={columns} />

          {isImporting && <ImportProgress currentRow={currentRow} totalRows={totalRows} />}

          {importResults.length > 0 && !isImporting && (
            <ImportResults importResults={importResults} />
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetForm}>
              Reset
            </Button>
            {csvData.length > 0 && validationErrors.length === 0 && (
              <Button onClick={importData} disabled={isImporting} className="gap-2">
                {isImporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Import {csvData.length} Items
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default GenericCsvImportModal;
